import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import LocationOnOutlinedIcon from "@mui/icons-material/LocationOnOutlined";
import MovieCreationOutlinedIcon from "@mui/icons-material/MovieCreationOutlined";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { selectEnvironmentInteractionPoints } from "../../app/slices/EnvironmentSlice";
import {
  closeSequencePositionPicker,
  openSequencePositionPicker,
  selectSequencePositionPicker,
} from "../../app/slices/GlobalSlice";
import {
  selectSelectedTake3D,
  setSelectedTake,
  updateSelectedTake,
} from "../../app/slices/TrainingSlice";
import PiInputfield from "../../components/PiInputField";
import Take from "../../models/Take";
import EditorColumn, {
  EditorColumnFieldGroup,
  EditorColumnHeader,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";

export default function Take3DPropertiesColumn() {
  const dispatch = useDispatch();
  const take = useSelector(selectSelectedTake3D);
  const takePositions = useSelector(selectEnvironmentInteractionPoints) ?? [];
  const sequencePositionPicker = useSelector(selectSequencePositionPicker);
  const { t } = useTranslation();

  const onChangeTakeName = (newName: string) => {
    dispatch(updateSelectedTake({ ...take, name: newName } as Take));
  };

  const currentLocationName = take?.interactionPointId
    ? takePositions.find((point) => point.id === take.interactionPointId)
        ?.name ?? "Unassigned"
    : "Unassigned";

  const isUnassigned = currentLocationName === "Unassigned";

  const isLocationPickerOpen =
    Boolean(sequencePositionPicker?.isOpen) &&
    sequencePositionPicker?.positionType === "takeLocation";

  const onToggleLocationPicker = () => {
    if (isLocationPickerOpen) {
      dispatch(closeSequencePositionPicker());
      return;
    }
    if (take) {
      dispatch(setSelectedTake(take.id));
    }
    dispatch(
      openSequencePositionPicker({
        nodeId: "",
        positionType: "takeLocation",
        title: "Pick take location",
      })
    );
  };

  return (
    <EditorColumn
      testId="take3d-properties-column"
      widthClassName="w-full min-w-0"
      borderSide="left"
      header={
        <EditorColumnHeader
          icon={<MovieCreationOutlinedIcon fontSize="inherit" />}
          title={take?.name ?? "Take"}
          testId="take3d-properties-header"
        />
      }
    >
      {take && (
        <EditorColumnSection
          title={t("take")}
          icon={<MovieCreationOutlinedIcon fontSize="inherit" />}
          defaultExpanded
          testId="take3d-properties-take-section"
        >
          <EditorColumnFieldGroup>
            <PiInputfield
              label={t("take_name")}
              value={take.name}
              placeholder={"Take Name"}
              onChange={onChangeTakeName}
              square
            />
          </EditorColumnFieldGroup>
          <EditorColumnFieldGroup testId="take3d-location-field">
            <div className="flex flex-col gap-[8px]">
              <div className="font-medium text-creator-text text-[14px]">
                {t("location")}
              </div>
              <div
                className={
                  "flex h-[36px] w-full items-center gap-[8px] rounded-[5px] border px-[10px] transition-all duration-150 " +
                  (isLocationPickerOpen
                    ? "border-creator-primary shadow-[0_0_0_3px_rgba(58,196,255,0.18)]"
                    : "border-[var(--pits-menu-border)] hover:border-creator-primary-faded")
                }
              >
                <LocationOnOutlinedIcon
                  fontSize="inherit"
                  className="shrink-0 text-[16px] text-creator-text-sub"
                />
                <div
                  className={
                    "flex-1 min-w-0 truncate text-[13px] " +
                    (isLocationPickerOpen
                      ? "text-creator-text-key"
                      : isUnassigned
                        ? "text-creator-text-sub"
                        : "text-creator-text")
                  }
                  data-testid="take3d-location-name"
                >
                  {isLocationPickerOpen
                    ? "Click a spot on the map…"
                    : currentLocationName}
                </div>
                <button
                  type="button"
                  title="Pick on map"
                  data-testid="pick-location-on-map-button"
                  disabled={takePositions.length === 0}
                  className={
                    "flex h-[26px] w-[26px] shrink-0 items-center justify-center rounded-[4px] transition-colors duration-150 disabled:opacity-60 disabled:cursor-not-allowed " +
                    (isLocationPickerOpen
                      ? "bg-creator-primary text-white"
                      : "text-creator-primary hover:bg-creator-primary-washed")
                  }
                  onClick={onToggleLocationPicker}
                >
                  <GpsFixedIcon fontSize="inherit" className="text-[16px]" />
                </button>
              </div>
            </div>
          </EditorColumnFieldGroup>
        </EditorColumnSection>
      )}
    </EditorColumn>
  );
}
